"use client";

import React, { useState, useEffect } from "react";
import { loadRazorpayScript } from "../utils/loadRazorpay";
import { useNavigate } from "react-router-dom";

const Shop = () => {
  const navigate = useNavigate();

  // State for products fetched from backend
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Other states
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCustomizeForm, setShowCustomizeForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [customizeData, setCustomizeData] = useState({
    name: "",
    email: "",
    whatsapp: "",
    message: "",
    colors: "",
    size: "",
    text: "",
    Quantity: "",
    state: "",
    district: "",
    pin: "",
    flat: "",
    apartment: "",
    area: "",
    productName: "",
    instructions: "",
    image: null,
  });

  // Fetch products from backend API on mount
  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch("https://rangdesaaj.onrender.com/api/products");
        if (!res.ok) throw new Error("Failed to fetch products");
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error(err);
        
      } finally {
        setLoadingProducts(false);
      }
    }
    fetchProducts();
  }, []);

  // Filter products by category
  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((product) => product.category === selectedCategory);

  // Handle Buy button click
  const handleBuyClick = (product) => {
    setSelectedProduct(product);
    setShowCustomizeForm(true);
    setCustomizeData({
      name: "",
      email: "",
      whatsapp: "",
      message: "",
      Quantity: "",
      colors: "",
      size: "",
      text: "",
      state: "",
      district: "",
      pin: "",
      flat: "",
      apartment: "",
      area: "",
      productName: "",
      instructions: "",
      image: null,
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomizeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle image file input change
  const handleImageChange = (e) => {
    setCustomizeData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  // Navigate to product detail page
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Close customization form modal
  const closeCustomizeForm = () => {
    setShowCustomizeForm(false);
    setSelectedProduct(null);
  };

  // Handle order submission and payment
  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    let uploadedImageUrl = null;

    // Upload image if provided
    if (customizeData.image) {
      const formData = new FormData();
      formData.append("image", customizeData.image);

      setLoading(true);
      try {
        const imageUploadRes = await fetch(
          "https://rangdesaaj.onrender.com/api/upload-image",
          {
            method: "POST",
            body: formData,
          }
        );

        const imageUploadData = await imageUploadRes.json();

        if (!imageUploadData.url) throw new Error("No URL returned");
        uploadedImageUrl = imageUploadData.url;
      } catch (err) {
        console.error(err);
        setLoading(false);
        alert("⚠️ Image upload failed. Try again.");
        return;
      }
    }

    // Load Razorpay SDK dynamically
    const res = await loadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load.");
      return;
    }

    // Create Razorpay order on backend
    let orderData;
    try {
      const orderRes = await fetch("https://rangdesaaj.onrender.com/api/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: selectedProduct.price * customizeData.Quantity * 100,
          productName: selectedProduct.name,
          email: customizeData.email,
          currency: "INR",
        }),
      });

      orderData = await orderRes.json();
      if (!orderData.id) throw new Error("Order creation failed");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to create Razorpay order");
      return;
    }

    // Razorpay payment options
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: selectedProduct.price * customizeData.Quantity * 100,
      currency: orderData.currency,
      name: "RangDesaaj",
      description: selectedProduct.name,
      image: "/logo.png",
      order_id: orderData.id,
      handler: async function (response) {
        try {
          const verifyRes = await fetch(
            "https://rangdesaaj.onrender.com/api/verify-payment",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ...customizeData,
                imageUrl: uploadedImageUrl,
                razorpayOrderId: orderData.id,
                razorpay_payment_id: response.razorpay_payment_id,
                amount: selectedProduct.price,
                currency: orderData.currency,
                productName: selectedProduct.name,
              }),
            }
          );

          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            alert("🎉 Payment & Order Successful!");
            setLoading(false);
            setShowCustomizeForm(false);
            setSelectedProduct(null);
          } else {
            alert("⚠️ Payment succeeded, but order storing failed.");
            setLoading(false);
          }
        } catch (err) {
          console.error(err);
          alert("❌ Payment received but order verification failed.");
          setLoading(false);
        }
      },
      prefill: {
        name: customizeData.name,
        email: customizeData.email,
        contact: customizeData.whatsapp,
      },
      notes: {
        message: customizeData.message,
        colors: customizeData.colors,
        size: customizeData.size,
        text: customizeData.text,
        Quantity: customizeData.Quantity,
      },
      theme: {
        color: "#000000",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Categories for filtering
  const categories = [
    { id: "all", name: "All Products" },
    { id: "coasters", name: "Coasters" },
    { id: "trays", name: "Trays" },
    { id: "art", name: "Wall Art" },
    { id: "decor", name: "Home Decor" },
    { id: "accessories", name: "Accessories" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Customize Form Modal */}
      {showCustomizeForm && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Customize Your Order
              </h2>
              <button
                onClick={closeCustomizeForm}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Selected Product Info */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center space-x-4">
                <img
                  src={selectedProduct.image || "/placeholder.svg"}
                  alt={selectedProduct.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-bold text-gray-900">
                    {selectedProduct.name}
                  </h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <p className="text-xl font-bold text-black">
                      ₹{selectedProduct.price}
                    </p>
                    <p className="text-xl text-gray-500 font-bold text-black line-through">
                      ₹{selectedProduct.mainPrice}
                    </p>
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Customization Form */}
            <form onSubmit={handleSubmitOrder} className="p-6 space-y-6">
              {/* Welcome Message */}
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {"Let's Customize Together! 🎨"}
                </h3>
                <p className="text-gray-600 text-sm">
                  {
                    "Tell us how you'd like to customize this piece - colors, text, size, or any special requests!"
                  }
                </p>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={customizeData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={customizeData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="whatsapp"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  WhatsApp (Optional)
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  value={customizeData.whatsapp}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              {/* Customization Options */}
              {selectedProduct.needsColor && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="colors"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Preferred Colors
                    </label>
                    <input
                      type="text"
                      id="colors"
                      name="colors"
                      value={customizeData.colors}
                      onChange={handleInputChange}
                      placeholder="e.g., Blue, Gold, White"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {selectedProduct.needsCustomText && (
                <div>
                  <label
                    htmlFor="text"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Custom Text (if applicable)
                  </label>
                  <input
                    type="text"
                    id="text"
                    name="text"
                    value={customizeData.text}
                    onChange={handleInputChange}
                    placeholder="Any text you'd like added to the piece"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              )}

              {selectedProduct.needsRequest && (
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Special Requests & Additional Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={customizeData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about any special requests, design ideas, or inspiration you have for this piece..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="Quantity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Quantity *
                </label>
                <input
                  type="tel"
                  id="Quantity"
                  name="Quantity"
                  required
                  value={customizeData.Quantity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              {selectedProduct.needsImage && (
                <div>
                  <label
                    htmlFor="image"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Reference Image (Optional)
                  </label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Upload any reference images or inspiration photos
                  </p>
                </div>
              )}

              <hr />
              <h4 className="font-semibold text-gray-900 mb-2">
                Delivery Address
              </h4>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={customizeData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  State *
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  required
                  value={customizeData.state}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="district"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  District *
                </label>
                <input
                  type="text"
                  id="district"
                  name="district"
                  required
                  value={customizeData.district}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="pin"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Pin Code *
                </label>
                <input
                  type="tel"
                  id="pin"
                  name="pin"
                  required
                  value={customizeData.pin}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="flat"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  House/Flat No *
                </label>
                <input
                  type="tel"
                  id="flat"
                  name="flat"
                  required
                  value={customizeData.flat}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="apartment"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Apartment Name
                </label>
                <input
                  type="text"
                  id="apartment"
                  name="apartment"
                  value={customizeData.apartment}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="area"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Area *
                </label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  required
                  value={customizeData.area}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="instructions"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Instructions to First Delivery *
                </label>
                <textarea
                  id="instructions"
                  name="instructions"
                  required
                  rows={4}
                  value={customizeData.instructions}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              {/* Pricing Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Pricing Information
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Selling Price: ₹{selectedProduct.price}</p>
                  <p>• MRP: ₹{selectedProduct.mainPrice}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeCustomizeForm}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-black hover:bg-gray-800 text-white"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Place Custom Order"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hero Header Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our Resin Art Collection
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Showcase banner with vibrant images of featured resin products and a
            brief introduction.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category.id
                    ? "bg-black text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loadingProducts ? (
            <p className="text-center text-gray-600">Loading products...</p>
          ) : filteredProducts.length === 0 ? (
            <p className="text-center text-gray-600">
              No products found in this category.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group cursor-pointer bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => handleProductClick(product.id)}
                >
                  <div className="bg-gray-200 rounded-lg aspect-square mb-4 overflow-hidden">
                    <img
                      src={product.image || "/placeholder.svg"}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <p className="text-xl font-bold text-black">
                      ₹{product.price}
                    </p>
                    <p className="text-xl text-gray-500 font-bold text-black line-through">
                      ₹{product.mainPrice}
                    </p>
                  </div>
              {/*}    <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBuyClick(product);
                    }}
                    className="w-full bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Buy
                  </button>*/}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Pricing Information
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Clear pricing information for different types of resin products and
            customization options.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-4">Basic Items</h3>
              <p className="text-3xl font-bold text-black mb-4">₹25-50</p>
              <p className="text-gray-600">Coasters, small decor items</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-4">Medium Items</h3>
              <p className="text-3xl font-bold text-black mb-4">₹50-100</p>
              <p className="text-gray-600">Trays, jewelry dishes, bookends</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-4">Large Items</h3>
              <p className="text-3xl font-bold text-black mb-4">₹100+</p>
              <p className="text-gray-600">Wall art, custom sculptures</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
